import { Tooltip, TooltipProps } from '@sgme/ui';
import type { ColDef, ICellRendererParams, IFloatingFilterParams } from 'ag-grid-community';
import classNames from 'classnames';
import type { FC, PropsWithChildren } from 'react';
import { useCallback } from 'react';

import { CustomIconSpinner } from '../../../../../utils/customSpinner';

type IconButtonProps = {
  onClick?: () => void;
  isDisabled?: boolean | undefined;
  icon: string;
  dataE2e?: string | undefined;
  title?: string | undefined;
  className?: string | undefined;
  location?: TooltipProps['position'];
};

export const IconActionButton: FC<PropsWithChildren<IconButtonProps>> = ({
  onClick,
  isDisabled,
  icon,
  dataE2e,
  title,
  className,
  location,
}) => {
  return (
    <Tooltip position={location ?? 'top'} contents={title}>
      <button
        className={classNames('btn btn-flat-primary w-100', className)}
        data-e2e={dataE2e ?? `${icon}-btn`}
        disabled={isDisabled ?? false}
        onClick={onClick}
      >
        <i className="icon">{icon}</i>
      </button>
    </Tooltip>
  );
};

export type IconActionCellRendererProps<TData = any> = ICellRendererParams<TData> &
  Omit<IconButtonProps, 'onClick'> & {
    onClick: (data: TData) => void;
    isBusy?: boolean;
    getIsVisible?: (data: TData) => boolean;
  };
export const IconActionCellRenderer: FC<PropsWithChildren<IconActionCellRendererProps>> = ({
  data,
  onClick,
  isBusy,
  getIsVisible,
  ...props
}) => {
  const onClickWithRowData = useCallback(() => {
    if (getIsVisible === undefined || getIsVisible(data)) {
      onClick(data);
    }
  }, [data, onClick, getIsVisible]);
  const isVisible = getIsVisible !== undefined ? getIsVisible(data) : true;
  if (!isVisible) {
    return null;
  }

  return isBusy ? (
    <CustomIconSpinner size="sm" className="d-flex justify-content-center" color="text-info" />
  ) : (
    <IconActionButton onClick={onClickWithRowData} {...props} />
  );
};

export type IconActionFloatingFilterProps = IFloatingFilterParams<any> &
  IconButtonProps & {
    isBusy?: boolean;
  };
export const IconActionFloatingFilterComponent: FC<PropsWithChildren<IconActionFloatingFilterProps>> = ({
  isBusy,
  ...props
}) =>
  isBusy ? (
    <CustomIconSpinner size="sm" className="d-flex justify-content-center" color="text-info" />
  ) : (
    <IconActionButton {...props} />
  );
type BuildIconOptions<T> = {
  icon: string;
  title?: string;
  e2e?: string;
  getIsVisible?: (data: T) => boolean;
  onClick: (data: T) => void;
};
type BuildIconActionFloatingFilterOption<T> = BuildIconOptions<T> & { floatingFilterComponent?: any };
export function buildIconActionFloatingFilter<T>({
  onClick,
  icon,
  title,
  e2e,
  getIsVisible,
  floatingFilterComponent,
}: BuildIconActionFloatingFilterOption<T>): ColDef<T> {
  return {
    floatingFilter: true,
    filter: true,
    floatingFilterComponent: floatingFilterComponent ?? IconActionFloatingFilterComponent,
    suppressFloatingFilterButton: true,
    floatingFilterComponentParams: {
      onClick: onClick,
      icon: icon,
      dataE2e: e2e,
      title: title,
      getIsVisible,
    },
  };
}

type BuildIconActionOption<T> = BuildIconOptions<T> & { cellRenderer?: any };
export function buildIconAction<T>({
  cellRenderer,
  icon,
  onClick,
  e2e,
  title,
  getIsVisible,
}: BuildIconActionOption<T>) {
  return {
    cellRenderer: cellRenderer ?? IconActionCellRenderer,
    cellRendererParams: {
      onClick: onClick,
      icon: icon,
      dataE2e: e2e,
      title: title,
      // className: 'py-2 px-4 mx-n3',
      getIsVisible,
    },
  };
}
